import type { ReactNode } from "react";
import "./settings.css";

export interface AdminTab<Id extends string> {
  id: Id;
  label: string;
  /** The one-line lead the header shows while this tab is selected. */
  lead: string;
  /** A tab the principal may not open is not rendered at all. */
  visible: boolean;
  /**
   * Opt this tab out of the 880px reading column.
   *
   * The column is sized for forms and lists read top to bottom. A tab that
   * carries a two-column workspace — Thinking models, once the agent studio
   * moved into it — needs the width or the roster rail squeezes the editor
   * into a gutter.
   */
  wide?: boolean;
}

interface Props<Id extends string> {
  title: string;
  tabs: AdminTab<Id>[];
  active: Id;
  onSelect: (id: Id) => void;
  children: ReactNode;
}

/**
 * The chrome both administration screens share.
 *
 * The canvas draws one administration layout and fills it twice: a 24/32
 * header carrying the title, the lead for the selected tab and the tab strip
 * on the rule beneath it, then the content in an 880px column. "Copilot
 * administration" (Business integrations, Thinking models) and "Settings"
 * (Branding & colors, Users & roles, Audit log) are that same layout with
 * different tabs, so it lives here rather than being transcribed twice.
 *
 * Tabs are filtered by the caller's permission check before they arrive: a tab
 * that would 403 is absent rather than present-and-disabled, because a tab
 * strip is a map of what this screen holds for *you*.
 */
export function AdminScreen<Id extends string>({
  title,
  tabs,
  active,
  onSelect,
  children,
}: Props<Id>): JSX.Element {
  const visible = tabs.filter((tab) => tab.visible);
  const current = visible.find((tab) => tab.id === active) ?? visible[0] ?? null;

  return (
    <div className="set-page">
      {/* The shell renders no route title bar, so the screen states its own
          heading. */}
      <header className="set-head">
        <h1 className="page-title">{title}</h1>
        <p className="page-subtitle">{current?.lead ?? ""}</p>
        {visible.length > 0 && (
          <div className="tabs" role="tablist">
            {visible.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={tab.id === current?.id}
                className="tab"
                onClick={() => onSelect(tab.id)}
                data-testid={`settings-tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}
      </header>

      <div className="set-body">
        <div className={current?.wide ? "set-column set-column-wide" : "set-column"}>
          {children}
        </div>
      </div>
    </div>
  );
}
